/**
 * SamarbeidSok – egen side som viser KUN resultatene fra samarbeid-søket.
 * Åpnes som ny fane fra CollabFind.runSearch med søkekriterier i URL-params.
 */
import React, { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Search, Users, ShieldCheck, Send, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { formatFollowers } from "../components/collab/CollabAccounts";
import CollabRequestComposer from "../components/collab/CollabRequestComposer";

export default function SamarbeidSok() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [sp, setSp] = useSearchParams();
  const [results, setResults] = useState(null);
  const [mySlots, setMySlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeFor, setComposeFor] = useState(null);

  const type = sp.get("type") || "";
  const platform = sp.get("platform") || "";
  const dates = sp.get("dates") || "";
  const dateStart = sp.get("date_start") || "";
  const dateEnd = sp.get("date_end") || "";
  const followerMin = sp.get("follower_min") || "";
  const followerMax = sp.get("follower_max") || "";
  const onlyMatching = sp.get("only_matching") === "1";

  const toggleOnlyMatching = () => {
    const next = new URLSearchParams(sp);
    if (onlyMatching) next.delete("only_matching");
    else next.set("only_matching", "1");
    setSp(next, { replace: true });
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const params = {};
    if (type) params.type = type;
    if (platform) params.platform = platform;
    if (dates) params.dates = dates;
    else {
      if (dateStart) params.date_start = dateStart;
      if (dateEnd) params.date_end = dateEnd;
    }
    if (followerMin) params.follower_min = Number(followerMin);
    if (followerMax) params.follower_max = Number(followerMax);
    if (onlyMatching) params.only_matching = 1;
    const run = async () => {
      setLoading(true);
      try {
        const r = await api.get("/collab/find", { params });
        if (!cancelled) {
          setResults(r.data?.results || []);
          setMySlots(r.data?.my_slots || []);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || t("collab.search_err", "Søk feilet."));
          setResults([]);
          setMySlots([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user, type, platform, dates, dateStart, dateEnd, followerMin, followerMax, onlyMatching, t]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_creator && !["admin", "owner"].includes(user.role)) {
    return <Navigate to="/skaper/samarbeid" replace />;
  }

  const summaryBits = [];
  if (type) summaryBits.push(type.toUpperCase());
  if (platform) summaryBits.push(platform.toUpperCase());
  if (dates) summaryBits.push(`${dates.split(",").length} ${t("collab.dates_short", "datoer")}`);
  else if (dateStart || dateEnd) summaryBits.push(`${dateStart || "…"} → ${dateEnd || "…"}`);
  if (followerMin) summaryBits.push(`min ${followerMin}`);
  if (followerMax) summaryBits.push(`max ${followerMax}`);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="samarbeid-sok-page">
        <div className="mb-6">
          <Link
            to="/skaper/samarbeid?tab=find"
            className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
            data-testid="samarbeid-sok-back"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("collab.back_to_search", "Tilbake til søk")}
          </Link>
        </div>

        <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-2 flex items-center gap-3">
          <Search className="h-7 w-7 text-[var(--magenta)]" />
          {t("collab.search_results_title", "Søkeresultat")}
        </h1>
        {summaryBits.length > 0 && (
          <p className="font-meta text-[11px] tracking-[0.2em] text-[var(--cream-faint)] mb-4 uppercase">
            {summaryBits.join(" · ")}
          </p>
        )}

        {/* Filter-toggle: kun felles datoer */}
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="only-matching-toggle-label">
            <input
              type="checkbox"
              checked={onlyMatching}
              onChange={toggleOnlyMatching}
              className="w-4 h-4 accent-[var(--magenta)]"
              data-testid="only-matching-toggle"
            />
            <span className="text-sm text-[var(--cream-soft)]">
              {t("collab.only_matching_label", "Vis kun skapere med felles ledige datoer")}
            </span>
          </label>
          <span className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] inline-flex items-center gap-1.5">
            <Star className="h-3 w-3 fill-[var(--gold)] text-[var(--gold)]" />
            {t("collab.match_legend", "= du er også ledig")}
          </span>
        </div>

        {onlyMatching && mySlots.length === 0 && !loading && (
          <div className="panel-tight p-4 mb-4 text-sm text-[var(--cream-soft)]" data-testid="no-my-slots-notice">
            {t("collab.no_my_slots", "Du har ingen ledige datoer i denne perioden – legg til datoer i kalenderen for å bruke dette filteret.")}
          </div>
        )}

        {loading ? (
          <div className="panel-tight p-12 text-center text-[var(--cream-soft)] font-fraunces italic">
            {t("collab.searching", "Søker…")}
          </div>
        ) : results === null || results.length === 0 ? (
          <div className="panel-tight p-12 text-center text-[var(--cream-soft)] font-fraunces italic">
            {t("collab.no_matches", "Ingen treff på dine kriterier.")}
          </div>
        ) : (
          <>
            <p className="font-meta text-xs text-[var(--cream-faint)] mb-3">
              {t("collab.found_n", { count: results.length, defaultValue: "{{count}} skaper(e) funnet" })}
            </p>
            <ul className="space-y-3">
              {results.map((r) => (
                <CreatorResultCard
                  key={r.creator.id}
                  item={r}
                  platformFilter={platform}
                  onCompose={(slot) =>
                    setComposeFor({ creator: r.creator, slot, accounts: r.accounts })
                  }
                />
              ))}
            </ul>
          </>
        )}

        {composeFor && (
          <CollabRequestComposer
            creator={composeFor.creator}
            slot={composeFor.slot}
            accounts={composeFor.accounts}
            defaultType={type}
            defaultPlatform={platform}
            onClose={() => setComposeFor(null)}
            onSent={() => setComposeFor(null)}
          />
        )}
      </div>
    </Layout>
  );
}

function CreatorResultCard({ item, platformFilter, onCompose }) {
  const { t } = useTranslation();
  const c = item.creator;
  const relevant = platformFilter
    ? item.accounts.filter((a) => a.platform === platformFilter)
    : item.accounts;
  const score = c.collab_reliability_score;

  return (
    <li className="panel-tight p-4" data-testid={`find-result-${c.username}`}>
      <div className="flex items-start gap-3">
        {c.avatar_url ? (
          <img src={c.avatar_url} alt="" className="w-12 h-12 object-cover border border-white/10" />
        ) : (
          <div className="w-12 h-12 bg-[var(--magenta)]/20 border border-white/10 flex items-center justify-center font-meta text-lg text-[var(--magenta)]">
            {(c.username || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <Link
              to={`/u/${c.username}`}
              className="font-fraunces italic text-xl text-[var(--cream)] hover:text-[var(--magenta)]"
            >
              {c.creator_display_name || c.username}
            </Link>
            <span className="text-xs text-[var(--cream-faint)]">@{c.username}</span>
            {score !== null && score !== undefined && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[var(--gold)]/30 text-[var(--gold)] font-meta text-[10px]">
                <ShieldCheck className="h-3 w-3" /> {score}% ·{" "}
                {item.creator.collab_completed_count || 0} {t("collab.completed_short", "fullført")}
              </span>
            )}
          </div>
          {c.creator_bio && (
            <p className="text-sm text-[var(--cream-soft)] mt-1 line-clamp-2">{c.creator_bio}</p>
          )}
        </div>
      </div>

      {relevant.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {relevant.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-1.5 px-2 py-1 border border-white/10 text-xs text-[var(--cream-soft)]"
            >
              <span className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)]">
                {a.platform.toUpperCase()}
              </span>
              <span>@{a.handle}</span>
              <Users className="h-3 w-3 text-[var(--cream-faint)]" />
              <span>{formatFollowers(a.follower_count)}</span>
            </span>
          ))}
        </div>
      )}

      <div className="mt-3">
        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-1.5">
          {t("collab.available_dates", "Ledige datoer (klikk for å sende forespørsel)")}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {item.available_slots.slice(0, 12).map((s) => (
            <button
              key={s.id}
              onClick={() => onCompose(s)}
              className={
                "px-2 py-1 text-xs inline-flex items-center gap-1.5 border transition-colors " +
                (s.you_available
                  ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10 hover:bg-[var(--gold)]/20"
                  : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]")
              }
              title={s.you_available ? t("collab.you_available_tt", "Du er også ledig denne dagen") : undefined}
              data-testid={s.you_available ? `find-slot-${s.date}-match` : `find-slot-${s.date}`}
            >
              {s.you_available && <Star className="h-3 w-3 fill-current" />}
              {s.date}
              <Send className="h-3 w-3" />
            </button>
          ))}
          {item.available_slots.length > 12 && (
            <span className="px-2 py-1 text-xs text-[var(--cream-faint)]">
              +{item.available_slots.length - 12}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
